import React, { useEffect, useState } from 'react';
import {
  Container,
  Form,
  Input,
  Button,
  Table,
  Spinner
} from 'reactstrap';
import { createNewProject, getTeamLeads } from '../../Api/ApiUrlConst';
import { useAuth } from '../../Context/AuthContext';
import { toast } from 'react-toastify';

interface Props {
  onProjectAdd: () => void;
}

interface TeamLead {
  id: string;
  name: string;
  department: string;
}

interface Project {
  ProjectID: string;
  ProjectName: string;
  Description: string;
  TeamLeadIds: string[];
  DepartmentName: string;
  StartDate?: string;
  EndDate?: string;
  Status: string;
  Remarks: string;
}

const AddNewProjectComponents: React.FC<Props> = ({ onProjectAdd }) => {
  const { userID } = useAuth();

  const [project, setProject] = useState<Project>({
    ProjectID: '',
    ProjectName: '',
    Description: '',
    TeamLeadIds: [],
    DepartmentName: '',
    StartDate: '',
    EndDate: '',
    Status: '',
    Remarks: ''
  });

  // Master list of all team leads
  const [teamLeads, setTeamLeads] = useState<TeamLead[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTeamLeads = async () => {
      try {
        const response = await getTeamLeads();
        const data = Array.isArray(response.data) ? response.data : [];
        const formatted = data.map((user: any) => ({
          id: user.userID,
          name: user.name,
          department: user.department,
        }));
        setTeamLeads(formatted);
      } catch (err) {
        console.error('Failed to fetch team leads:', err);
      }
    };
    fetchTeamLeads();
  }, []);

  // ✅ handleChange with DepartmentName fix
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "DepartmentName") {
      let newTeamLeadIds: string[] = [];

      if (value === "All") {
        // Select all TLs
        newTeamLeadIds = teamLeads.map(tl => tl.id.toString());
      } else {
        // Filter TLs by chosen department
        newTeamLeadIds = teamLeads
          .filter(tl => tl.department === value)
          .map(tl => tl.id.toString());
      }

      setProject(prev => ({
        ...prev,
        DepartmentName: value,
        TeamLeadIds: newTeamLeadIds,
      }));

      return;
    }

    // Default: handle other input fields
    setProject(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return; // 🔒 extra safety

    if (project.TeamLeadIds.length === 0) {
      toast.warning('Please select at least one Team Lead.');
      return;
    }

    if (new Date(project.StartDate || '') >= new Date(project.EndDate || '')) {
      toast.warning('End Date must be after Start Date.');
      return;
    }

    try {
      setIsSubmitting(true); // 🔒 disable button

      await createNewProject(project);

      toast.success(`Project "${project.ProjectName}" has been created and assigned`);

      setProject({
        ProjectID: '',
        ProjectName: '',
        Description: '',
        TeamLeadIds: [],
        DepartmentName: '',
        StartDate: '',
        EndDate: '',
        Status: '',
        Remarks: ''
      });

      onProjectAdd();
    } catch (err: any) {
      console.error('Project creation failed:', err);
      const message = err.response?.data?.message || 'Failed to create project';
      toast.error(`❌ ${message}`);
    } finally {
      setIsSubmitting(false); // 🔓 enable button again
    }
  };


  const projectStatusOptions = [
    { value: "YetToStart", label: "Yet to Start" },
    { value: "WaitingForInput", label: "Waiting for Input" },
    { value: "", label: "" },
    { value: "ModellingTodo", label: "Modelling ToDo" },
    { value: "ModellingInProgress", label: "Modelling in Progress" },
    { value: "ModellingCompleted", label: "Modelling Completed" },
    { value: "", label: "" },
    { value: "DraftingTodo", label: "Drafting ToDo" },
    { value: "DraftingInProgress", label: "Drafting in Progress" },
    { value: "DraftingCompleted", label: "Drafting Completed" },
    { value: "", label: "" },
    { value: "QCTodo", label: "QC ToDo" },
    { value: "QCInProgress", label: "QC in Progress" },
    { value: "QCCompleted", label: "QC Completed" },
    { value: "", label: "" },
    { value: "ClientReviewTodo", label: "Client Review ToDo" },
    { value: "ClientReviewInProgress", label: "Client Review in Progress" },
    { value: "ClientReviewCompleted", label: "Client Review Completed" },
    { value: "", label: "" },
    { value: "FinalCorrectionTodo", label: "Final Correction ToDo" },
    { value: "FinalCorrectionInProgress", label: "Final Correction in Progress" },
    { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
    { value: "", label: "" },
    { value: "RevisionTodo", label: "Revision ToDo" },
    { value: "RevisionInProgress", label: "Revision in Progress" },
    { value: "RevisionCompleted", label: "Revision Completed" },
    { value: "", label: "" },
    { value: "Cancelled", label: "Cancelled" },
    { value: 'Issues', label: 'Issues' },
    { value: "OnHold", label: "On Hold" },
    { value: "Released", label: "Released" },
    { value: "NotInScope", label: "Not In Scope" },
    { value: "Completed", label: "Ready for Release" },
  ];


  return (
    <Container fluid className="mt-4">
      <Form onSubmit={handleSubmit} className="mb-3">
        <Table responsive className="mt-3">
          <thead>
            <tr>
              <th style={{ width: '8%' }}>Project ID</th>
              <th style={{ width: '20%' }}>Project Name</th>
              <th style={{ width: '10%' }}>Description</th>
              <th style={{ width: '12%' }}>Department</th>
              {/* Removed Team Leads column - handled automatically */}
              <th style={{ width: '8%' }}>Status</th>
              <th style={{ width: '8%' }}>Start Date</th>
              <th style={{ width: '8%' }}>End Date</th>
              <th style={{ width: '15%' }}>Remarks</th>
              <th style={{ width: '2%' }}>+</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Input type="text" name="ProjectID" value={project.ProjectID} onChange={handleChange} required />
              </td>
              <td>
                <Input type="text" name="ProjectName" value={project.ProjectName} onChange={handleChange} required />
              </td>
              <td>
                <Input type="text" name="Description" value={project.Description} onChange={handleChange} />
              </td>
              <td>
                <Input
                  type='select'
                  name='DepartmentName'
                  value={project.DepartmentName}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Department</option>
                  <option value="All">All</option>
                  <option value="IT">IT</option>
                  <option value="Mechanical">Mechanical</option>
                  <option value="Electrical">Electrical</option>
                </Input>
              </td>
              <td>
                <Input type="select" name="Status" value={project.Status} onChange={handleChange} required>
                  <option value="">Select Status</option>
                  {projectStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </td>
              <td>
                <Input type="date" name="StartDate" value={project.StartDate} onChange={handleChange} required />
              </td>
              <td>
                <Input type="date" name="EndDate" value={project.EndDate} onChange={handleChange} required />
              </td>
              <td>
                <Input type="text" name="Remarks" value={project.Remarks} onChange={handleChange} />
              </td>
              <td>
                <Button color="primary" outline type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <Spinner size="sm" /> : <b>+</b>}
                </Button>


              </td>
            </tr>
          </tbody>
        </Table>
      </Form>
    </Container>
  );
};

export default AddNewProjectComponents;
